import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Badge, Card, CardMedia, Chip, CircularProgress, Grid, Paper, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useSubmission } from 'services/Data/SubmissionServices';
import { useCriteria, useCriteriaTypeDetails, useCriteriaTypes } from 'services/Data/CriteriaServices';
import { BlobStorageTypeEnumValue } from 'enums/BlobStorageTypeEnum';
import { HOST } from 'config/config';
import { translateLabel } from 'services/Utilities/labelUtils';
import SocialFeed from 'Components/SocialFeed';
import ImageWithCommentsDialog from './ImageWithCommentsDialog';
import { heicTo, isHeic } from 'heic-to/csp';
import { detailAppliesToSubmission, orderDetailsByHierarchy } from 'services/Utilities/criteriaDetailUtils';

const HEIC_EXTENSIONS = new Set(['heic', 'heif']);
const HEIC_MIME_TYPES = new Set(['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence']);
const HEIF_BRANDS = new Set(['heic', 'heix', 'hevc', 'hevx', 'heim', 'heis', 'heif', 'mif1', 'msf1']);

const isHeicFileType = (fileName, mimeType) => {
  const extension = fileName
    ?.split('.')
    ?.pop()
    ?.toLowerCase();
  const normalizedMimeType = mimeType?.toLowerCase();
  return HEIC_EXTENSIONS.has(extension) || HEIC_MIME_TYPES.has(normalizedMimeType);
};

const getBlobFormat = async blob => {
  if (!blob) {
    return 'unknown';
  }

  const normalizedMimeType = blob.type?.toLowerCase();
  if (HEIC_MIME_TYPES.has(normalizedMimeType)) {
    return 'heif';
  }
  if (normalizedMimeType === 'image/jpeg') {
    return 'jpeg';
  }
  if (normalizedMimeType === 'image/png') {
    return 'png';
  }

  try {
    const headerBuffer = await blob.slice(0, 64).arrayBuffer();
    const bytes = new Uint8Array(headerBuffer);

    if (bytes.length >= 12) {
      const boxType = String.fromCharCode(...bytes.slice(4, 8));
      if (boxType === 'ftyp') {
        const majorBrand = String.fromCharCode(...bytes.slice(8, 12)).toLowerCase();
        if (HEIF_BRANDS.has(majorBrand)) {
          return 'heif';
        }

        for (let offset = 16; offset + 4 <= bytes.length; offset += 4) {
          const compatibleBrand = String.fromCharCode(...bytes.slice(offset, offset + 4)).toLowerCase();
          if (HEIF_BRANDS.has(compatibleBrand)) {
            return 'heif';
          }
        }
      }
    }

    if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
      return 'jpeg';
    }

    if (
      bytes.length >= 8 &&
      bytes[0] === 0x89 &&
      bytes[1] === 0x50 &&
      bytes[2] === 0x4e &&
      bytes[3] === 0x47 &&
      bytes[4] === 0x0d &&
      bytes[5] === 0x0a &&
      bytes[6] === 0x1a &&
      bytes[7] === 0x0a
    ) {
      return 'png';
    }
  } catch (error) {
    return 'unknown';
  }

  return 'unknown';
};

const sortByOrder = (a, b) => (a?.order ?? 0) - (b?.order ?? 0);

const getCriteriaTypesToRender = (eligibleCriteriaTypes, criteria) =>
  eligibleCriteriaTypes.length ? eligibleCriteriaTypes : [...(criteria?.criteriaTypes || [])].sort(sortByOrder);

const getDetailsForType = (criteriaType, eligibleDetailsByType) => {
  const eligible = eligibleDetailsByType.get(criteriaType.id);
  if (eligible?.length) return eligible;
  return [...(criteriaType.criteriaTypeDetails || [])].sort(sortByOrder);
};

const translateDetailName = (detail, language) =>
  translateLabel(detail.description, language) || translateLabel(detail.name, language);

const buildImageEntries = ({ details, submissionDetails, language }) => {
  const images = [];

  details.forEach(detail => {
    const submissionDetail = submissionDetails.find(sd => sd.criteriaTypeDetail_Id === detail.id);
    if (!submissionDetail?.files?.length) return;

    submissionDetail.files.forEach(file => {
      const url = `${HOST}/api/dashboard/attached-files/${submissionDetail.id}/${
        file.id
      }/download?type=${file.blobStorageType || BlobStorageTypeEnumValue.CertificationTool}`;

      images.push({
        url,
        detailName: translateDetailName(detail, language),
        submissionDetailId: submissionDetail.id,
        fileId: file.id,
        fileName: file.fileName,
        mimeType: file.contentType || file.mimeType,
        postCount: submissionDetail.posts?.length || 0,
        posts: submissionDetail.posts || [],
      });
    });
  });

  return images;
};

// Step 4 summary view: show uploaded photos grouped by criteria type with a simple viewer
const SummaryView = ({ isReadOnly = false, hasAvailableCriteriaOptions = true }) => {
  const { submissionId } = useParams();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { data: submission } = useSubmission(submissionId);
  const { data: criteria } = useCriteria(submission?.criteria_Id);
  const { data: allCriteriaTypes } = useCriteriaTypes();
  const { data: criteriaTypeDetails } = useCriteriaTypeDetails(submissionId);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageSources, setImageSources] = useState({});
  const [convertingImageIds, setConvertingImageIds] = useState({});

  const attemptedCriteriaOptionId = submission?.attemptedCriteriaOption_Id || null;
  const heroUnitProductLineId = submission?.heroUnitProductLine_Id || submission?.heroUnitProductLine?.id || null;
  const requiresHeroUnitSelection = submission?.criteria?.isHeroUnitVisible !== false;

  const dealerProductLineIds = useMemo(() => {
    const productLines = submission?.dealer?.dealerProductLines || [];
    return productLines.map(line => line.id).filter(Boolean);
  }, [submission]);

  const submissionDetailValueByDetailId = useMemo(() => {
    const map = new Map();
    (submission?.submissionDetails || []).forEach(detail => {
      if (detail?.criteriaTypeDetail_Id) {
        map.set(detail.criteriaTypeDetail_Id, detail.value ?? '');
      }
    });
    return map;
  }, [submission?.submissionDetails]);

  const detailContext = useMemo(
    () => ({
      attemptedCriteriaOptionId,
      heroUnitProductLineId,
      dealerProductLineIds,
      submissionDetailValueByDetailId,
      requiresHeroUnitSelection,
      hasAvailableCriteriaOptions,
    }),
    [
      attemptedCriteriaOptionId,
      heroUnitProductLineId,
      dealerProductLineIds,
      submissionDetailValueByDetailId,
      requiresHeroUnitSelection,
      hasAvailableCriteriaOptions,
    ],
  );

  const eligibleDetailsByType = useMemo(() => {
    if (!criteriaTypeDetails || (requiresHeroUnitSelection && !heroUnitProductLineId)) return new Map();

    const grouped = new Map();
    criteriaTypeDetails.forEach(detail => {
      if (!detail?.criteriaType_Id) return;
      if (!detailAppliesToSubmission(detail, detailContext)) return;
      const existing = grouped.get(detail.criteriaType_Id) ?? [];
      existing.push(detail);
      grouped.set(detail.criteriaType_Id, existing);
    });

    const ordered = new Map();
    grouped.forEach((details, typeId) => {
      const orderedDetails = orderDetailsByHierarchy(details);
      if (orderedDetails.length > 0) {
        ordered.set(typeId, orderedDetails);
      }
    });

    return ordered;
  }, [criteriaTypeDetails, detailContext, heroUnitProductLineId, requiresHeroUnitSelection]);

  const eligibleCriteriaTypes = useMemo(() => {
    if (!allCriteriaTypes || !submission?.criteria_Id) return [];

    return allCriteriaTypes
      .filter(ct => ct.criteria_Id === submission.criteria_Id)
      .filter(ct => {
        const optionIds = ct.criteriaTypeCriteriaOptions?.map(join => join?.criteriaOption_Id) || [];
        if (attemptedCriteriaOptionId && !optionIds.includes(attemptedCriteriaOptionId)) return false;

        const productLineIds = ct.criteriaTypeProductLines?.map(join => join?.productLine_Id) || [];
        if (requiresHeroUnitSelection) {
          if (!heroUnitProductLineId || !productLineIds.includes(heroUnitProductLineId)) return false;
        } else if (heroUnitProductLineId && !productLineIds.includes(heroUnitProductLineId)) {
          return false;
        }

        const dealerLines = ct.criteriaTypeDealerProductLines?.map(join => join?.productLine_Id) || [];
        if (dealerLines.length && !dealerLines.some(id => dealerProductLineIds.includes(id))) return false;

        return true;
      })
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [
    allCriteriaTypes,
    submission?.criteria_Id,
    attemptedCriteriaOptionId,
    requiresHeroUnitSelection,
    heroUnitProductLineId,
    dealerProductLineIds,
  ]);

  const imagesByCriteriaType = useMemo(() => {
    if (!submission?.submissionDetails) return [];

    const criteriaTypesToRender = getCriteriaTypesToRender(eligibleCriteriaTypes, criteria);

    return criteriaTypesToRender
      .map(ct => {
        let images = [];
        const sortedDetails = [...(ct.criteriaTypeDetails || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

        sortedDetails.forEach(detail => {
          const submissionDetail = submission.submissionDetails.find(sd => sd.criteriaTypeDetail_Id === detail.id);
          if (submissionDetail?.files?.length) {
            submissionDetail.files.forEach(file => {
              const url = `${HOST}/api/dashboard/attached-files/${submissionDetail.id}/${
                file.id
              }/download?type=${file.blobStorageType || BlobStorageTypeEnumValue.CertificationTool}`;
              images.push({
                url,
                detailName: translateLabel(detail.description, i18n.language) || translateLabel(detail.name, i18n.language),
                submissionDetailId: submissionDetail.id,
                fileId: file.id,
                fileName: file.fileName,
                mimeType: file.contentType || file.mimeType,
                postCount: submissionDetail.posts?.length || 0,
                posts: submissionDetail.posts || [],
              });
            });
          }
        });
        const details = getDetailsForType(ct, eligibleDetailsByType);
        images = buildImageEntries({
          details,
          submissionDetails: submission.submissionDetails,
          language: i18n.language,
        });

        return { criteriaTypeName: translateLabel(ct.name, i18n.language), images };
      })
      .filter(group => group.images.length > 0);
  }, [submission, eligibleCriteriaTypes, criteria, eligibleDetailsByType, i18n.language]);

  useEffect(() => {
    let isMounted = true;
    const objectUrls = [];

    const allImages = imagesByCriteriaType.flatMap(group => group.images || []);

    const resolveImageSources = async () => {
      if (allImages.length === 0) {
        if (isMounted) {
          setImageSources({});
          setConvertingImageIds({});
        }
        return;
      }

      const convertingMap = {};

      allImages.forEach(image => {
        const imageKey = `${image.submissionDetailId}-${image.fileId}`;
        if (isHeicFileType(image.fileName, image.mimeType)) {
          convertingMap[imageKey] = true;
        }
      });

      if (isMounted) {
        setImageSources({});
        setConvertingImageIds(convertingMap);
      }

      await Promise.all(
        allImages.map(async image => {
          const imageKey = `${image.submissionDetailId}-${image.fileId}`;
          const looksLikeHeic = isHeicFileType(image.fileName, image.mimeType);

          if (!looksLikeHeic) {
            if (isMounted) {
              setImageSources(current => ({ ...current, [imageKey]: image.url }));
            }
            return;
          }

          try {
            const response = await fetch(image.url);
            if (!response.ok) {
              if (isMounted) {
                setImageSources(current => ({ ...current, [imageKey]: image.url }));
              }
              return;
            }

            const blob = await response.blob();
            const blobFormat = await getBlobFormat(blob);

            if (blobFormat !== 'heif') {
              const sourceUrl = URL.createObjectURL(blob);
              objectUrls.push(sourceUrl);
              if (isMounted) {
                setImageSources(current => ({ ...current, [imageKey]: sourceUrl }));
              }
              return;
            }

            const canDecodeHeif = await isHeic(blob);

            if (!canDecodeHeif) {
              const sourceUrl = URL.createObjectURL(blob);
              objectUrls.push(sourceUrl);
              if (isMounted) {
                setImageSources(current => ({ ...current, [imageKey]: sourceUrl }));
              }
              return;
            }

            const converted = await heicTo({ blob, type: 'image/jpeg', quality: 0.9 });
            const convertedBlob = converted instanceof Blob ? converted : new Blob([converted], { type: 'image/jpeg' });
            const convertedUrl = URL.createObjectURL(convertedBlob);
            objectUrls.push(convertedUrl);
            if (isMounted) {
              setImageSources(current => ({ ...current, [imageKey]: convertedUrl }));
            }
          } catch (error) {
            if (isMounted) {
              setImageSources(current => ({ ...current, [imageKey]: image.url }));
            }
          } finally {
            if (isMounted) {
              setConvertingImageIds(current => {
                if (!current[imageKey]) {
                  return current;
                }

                const updated = { ...current };
                delete updated[imageKey];
                return updated;
              });
            }
          }
        }),
      );

      if (!isMounted) {
        objectUrls.forEach(url => URL.revokeObjectURL(url));
      }
    };

    resolveImageSources();

    return () => {
      isMounted = false;
      objectUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [imagesByCriteriaType]);

  const summaryPhotoCount = useMemo(() => {
    if (!imagesByCriteriaType.length) return 0;
    return imagesByCriteriaType.reduce((acc, group) => acc + group.images.length, 0);
  }, [imagesByCriteriaType]);

  if (!submission || !criteria) return null;
  return (
    <Paper
      elevation={3}
      sx={{
        p: { xs: 2, md: 3 },
        border: theme => `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        bgcolor: theme.palette.background.paper,
      }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            {t('submission.summary')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('submission.photoCount', {
              count: summaryPhotoCount,
            })}
          </Typography>
        </Box>
      </Box>

      {imagesByCriteriaType.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('submission.noPhotosUploaded')}
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {imagesByCriteriaType.map(group => (
            <Grid item xs={12} key={group.criteriaTypeName}>
              <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {group.criteriaTypeName}
                </Typography>
                <Chip
                  label={t('submission.photoCount', { count: group.images.length })}
                  size="small"
                  sx={{
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,210,0,0.2)' : 'rgba(255,210,0,0.15)',
                    color: theme.palette.mode === 'dark' ? '#ffffff' : '#665200',
                  }}
                />
              </Box>

              <Grid container spacing={1.5}>
                {group.images.map((image, idx) => (
                  <Grid item xs={6} md={4} lg={3} key={`${group.criteriaTypeName}-${idx}`} sx={{ display: 'flex' }}>
                    {(() => {
                      const imageKey = `${image.submissionDetailId}-${image.fileId}`;
                      const imageUrl = imageSources[imageKey] || image.url;
                      const isConverting = Boolean(convertingImageIds[imageKey]);

                      return (
                        <Badge
                          badgeContent={image.postCount > 0 ? image.postCount : null}
                          color="primary"
                          sx={{
                            width: '100%',
                            display: 'flex',
                            '& .MuiBadge-badge': {
                              bgcolor: theme.palette.secondary.main,
                              color: theme.palette.getContrastText(theme.palette.secondary.main),
                              fontWeight: 700,
                              right: 6,
                              top: 6,
                            },
                          }}>
                          <Card
                            onClick={() => setSelectedImage({ ...image, resolvedUrl: imageUrl })}
                            sx={{
                              cursor: 'pointer',
                              overflow: 'hidden',
                              width: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              border: theme => `1px solid ${theme.palette.divider}`,
                              '&:hover': { borderColor: theme.palette.primary.main },
                            }}>
                            <Box sx={{ position: 'relative', width: '100%', height: 200 }}>
                              <CardMedia
                                component="img"
                                image={imageUrl}
                                alt={image.detailName}
                                sx={{ height: 200, width: '100%', objectFit: 'cover' }}
                              />
                              {isConverting && (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: 'rgba(0,0,0,0.35)',
                                  }}>
                                  <CircularProgress
                                    size={26}
                                    sx={{ color: theme.palette.mode === 'dark' ? theme.palette.primary.main : '#FFD200' }}
                                  />
                                </Box>
                              )}
                              <Box
                                sx={theme => ({
                                  position: 'absolute',
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  px: 1,
                                  py: 0.75,
                                  minHeight: 44,
                                  display: 'flex',
                                  alignItems: 'center',
                                  bgcolor:
                                    theme.palette.mode === 'dark'
                                      ? alpha(theme.palette.background.paper, 0.65)
                                      : alpha(theme.palette.background.paper, 0.75),
                                  backdropFilter: 'blur(4px)',
                                })}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 700,
                                    lineHeight: 1.2,
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                  }}>
                                  {image.detailName}
                                </Typography>
                              </Box>
                            </Box>

                            {image.postCount > 0 ? (
                              <Box
                                sx={theme => ({
                                  px: 1,
                                  py: 1,
                                  borderTop: `1px solid ${theme.palette.divider}`,
                                  bgcolor: theme.palette.background.paper,
                                  minHeight: 56,
                                })}>
                                {(() => {
                                  const posts = Array.isArray(image.posts) ? image.posts : [];
                                  const latestPost = posts
                                    .slice()
                                    .sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated))[0];

                                  if (!latestPost) return null;

                                  return (
                                    <Box>
                                      <Typography
                                        variant="body3"
                                        sx={{
                                          fontWeight: 700,
                                          display: 'block',
                                          color: theme => theme.palette.text.primary,
                                        }}>
                                        {latestPost.userName || t('social.anonymous')}
                                      </Typography>
                                      {latestPost.content ? (
                                        <Typography
                                          variant="body3"
                                          sx={theme => ({
                                            color: theme.palette.text.secondary,
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                            lineHeight: 1.2,
                                          })}>
                                          {latestPost.content}
                                        </Typography>
                                      ) : null}
                                    </Box>
                                  );
                                })()}
                              </Box>
                            ) : null}
                          </Card>
                        </Badge>
                      );
                    })()}
                  </Grid>
                ))}
              </Grid>
            </Grid>
          ))}
        </Grid>
      )}

      <Box sx={{ mt: 4 }}>
        <SocialFeed
          submissionId={submissionId}
          submissionDetailId={null}
          posts={submission?.posts || []}
          isLoading={false}
          isReadOnly={isReadOnly}
        />
      </Box>

      <ImageWithCommentsDialog
        open={Boolean(selectedImage)}
        onClose={() => setSelectedImage(null)}
        imageUrl={selectedImage?.resolvedUrl || selectedImage?.url}
        imageName={selectedImage?.detailName}
        submissionId={submissionId}
        submissionDetailId={selectedImage?.submissionDetailId}
        posts={selectedImage?.posts || []}
        isReadOnly={isReadOnly}
      />
    </Paper>
  );
};

export default SummaryView;
